/*!
 * BTM Animations - btm-animations.js
 * Beyond The Map Shopify Theme
 * Pure vanilla JS · No dependencies · Intersection Observer + rAF
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     Utilities
  ───────────────────────────────────────────── */
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Throttle a function to one call per animation frame. */
  function rafThrottle(fn) {
    let pending = false;
    return function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        fn();
        pending = false;
      });
    };
  }

  /** Clamp a value between lo and hi. */
  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  /* ─────────────────────────────────────────────
     1. Scroll Progress Bar
     Injects a fixed 3px gold bar that fills as the
     user scrolls through the page.
  ───────────────────────────────────────────── */
  function initProgressBar() {
    var bar = document.createElement('div');
    bar.id = 'btm-progress-bar';
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-hidden', 'true');
    document.body.prepend(bar);

    var update = rafThrottle(function () {
      var scrolled = window.scrollY || document.documentElement.scrollTop;
      var total = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.width = total > 0 ? (scrolled / total * 100) + '%' : '0%';
    });

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ─────────────────────────────────────────────
     2. Scroll Reveal - fade-up, slide-left/right, reveal-image
     Adds .is-visible when 15% of element enters viewport.
     Respects [data-delay="ms"] for manual stagger offsets.
  ───────────────────────────────────────────── */
  function initScrollReveal() {
    var els = document.querySelectorAll(
      '.btm-fade-up, .btm-slide-left, .btm-slide-right, .btm-reveal-image'
    );
    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.dataset.delay || '0', 10);
        setTimeout(function () { el.classList.add('is-visible'); }, delay);
        io.unobserve(el);
      });
    }, { threshold: 0.15 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ─────────────────────────────────────────────
     3. Staggered Card Grids
     Apply .btm-stagger to a grid/flex parent.
     JS sets incremental transition-delay (80ms per child)
     then adds .is-visible to trigger the cascade.
  ───────────────────────────────────────────── */
  function initStagger() {
    var containers = document.querySelectorAll('.btm-stagger');
    if (!containers.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var children = Array.from(entry.target.children);
        children.forEach(function (child, i) {
          child.style.transitionDelay = (i * 80) + 'ms';
        });
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.15 });

    containers.forEach(function (el) { io.observe(el); });
  }

  /* ─────────────────────────────────────────────
     4. Counter Animation
     Usage: class="btm-count-up" data-target="56"
            data-suffix="+" data-prefix=""
     With prefers-reduced-motion: shows final value instantly.
  ───────────────────────────────────────────── */
  function runCounter(el) {
    var raw     = el.dataset.target || '0';
    var target  = parseFloat(raw);
    var decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
    var prefix  = el.dataset.prefix || '';
    var suffix  = el.dataset.suffix || '';

    if (reduced) {
      el.textContent = prefix + target.toFixed(decimals) + suffix;
      return;
    }

    var duration = 1500;
    var start    = performance.now();

    function tick(now) {
      var p      = clamp((now - start) / duration, 0, 1);
      var eased  = 1 - Math.pow(1 - p, 3); // ease-out cubic
      var val    = eased * target;
      el.textContent = prefix + val.toFixed(decimals) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }

  function initCountUp() {
    var els = document.querySelectorAll('.btm-count-up[data-target]');
    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        runCounter(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.5 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ─────────────────────────────────────────────
     5. SVG Draw-on Effect
     Usage: class="btm-draw-on" on any <path> or <polyline>
     that uses a stroke (topo lines, trail lines, etc.)
     The path draws progressively as the user scrolls past it.
     Skipped automatically for prefers-reduced-motion.
  ───────────────────────────────────────────── */
  function initDrawOn() {
    var paths = document.querySelectorAll('.btm-draw-on');
    if (!paths.length) return;

    var targets = [];

    paths.forEach(function (path) {
      if (typeof path.getTotalLength !== 'function') return;
      var len = path.getTotalLength();
      path.style.strokeDasharray = len;
      // Reduced motion: show fully immediately
      path.style.strokeDashoffset = reduced ? 0 : len;
      if (!reduced) targets.push({ el: path, len: len });
    });

    if (!targets.length) return;

    var update = rafThrottle(function () {
      var vH = window.innerHeight;
      targets.forEach(function (t) {
        var rect     = t.el.getBoundingClientRect();
        // 0% drawn when element enters from bottom; 100% when top reaches viewport top
        var progress = clamp(1 - rect.top / vH, 0, 1);
        t.el.style.strokeDashoffset = t.len * (1 - progress);
      });
    });

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ─────────────────────────────────────────────
     6. Parallax Backgrounds
     Usage: class="btm-parallax" on position:absolute
     background elements (topo SVG, background images).
     Element moves at 50% of scroll speed via translateY.
     Skipped for prefers-reduced-motion.
  ───────────────────────────────────────────── */
  function initParallax() {
    if (reduced) return;

    var els = document.querySelectorAll('.btm-parallax');
    if (!els.length) return;

    var update = rafThrottle(function () {
      var vH = window.innerHeight;
      els.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        // Skip if far outside viewport
        if (rect.bottom < -vH || rect.top > vH * 2) return;
        // When element centre equals viewport centre: offset = 0
        var offset = (rect.top - vH / 2) * -0.5;
        el.style.transform = 'translateY(' + offset + 'px)';
      });
    });

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ─────────────────────────────────────────────
     7. Navbar Section Theme Detection
     Sections declare their background type via:
       data-btm-theme="dark"   (forest green bg)
       data-btm-theme="light"  (cream/stone bg)
     When a section becomes dominant near the top of
     the viewport, the navbar toggles .btm-navbar--light.
  ───────────────────────────────────────────── */
  function initNavbarTheme() {
    // Try section wrapper first, then inner element, then generic header
    var navbar =
      document.querySelector('#shopify-section-btm-navbar') ||
      document.querySelector('.btm-navbar') ||
      document.querySelector('header[role="banner"]');
    if (!navbar) return;

    var themedSections = document.querySelectorAll('[data-btm-theme]');
    if (!themedSections.length) return;

    var current = null;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var theme = entry.target.dataset.btmTheme;
        if (theme === current) return;
        current = theme;
        navbar.classList.toggle('btm-navbar--light', theme === 'light');
      });
    }, {
      // Fires when the section's top edge enters the 80px zone just below the navbar
      rootMargin: '-80px 0px -85% 0px',
      threshold: 0
    });

    themedSections.forEach(function (el) { io.observe(el); });
  }

  /* ─────────────────────────────────────────────
     8. ATC Button Click Animation + Cart Count
     Tracks the active submit button across all BTM
     product-form-component wrappers.
     States:
       .btm-atc--adding  - pulsing while fetch is in flight
       .btm-atc--added   - bounce once on success
     The 'cart:update' event (fired by Horizon after a
     successful /cart/add fetch) is the success signal.
     'cart:error' rolls back the adding state.
  ───────────────────────────────────────────── */
  function initATCAnimation() {
    var activeBtn = null;

    // Capture mousedown for the immediate press dimple
    // (CSS :active handles the visual; this just tracks which button)
    document.addEventListener('mousedown', function (e) {
      var btn = e.target.closest('product-form-component [type="submit"]');
      if (!btn) return;
      // Store reference early so cart:update can find it
      activeBtn = btn;
    }, true);

    // When the form actually submits, start the "adding" pulse
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('product-form-component form');
      if (!form) return;
      var btn = form.querySelector('[type="submit"]') || activeBtn;
      if (!btn) return;
      activeBtn = btn;
      btn.classList.remove('btm-atc--added');
      btn.classList.add('btm-atc--adding');
    }, true);

    // Success - stop pulse, play bounce
    document.addEventListener('cart:update', function (e) {
      // Only act when the source is our product-form-component
      var source = e.detail && e.detail.data && e.detail.data.source;
      if (source !== 'product-form-component') return;
      if (!activeBtn) return;

      var btn = activeBtn;
      activeBtn = null;

      btn.classList.remove('btm-atc--adding');
      btn.classList.add('btm-atc--added');

      // Clean up after animation completes
      setTimeout(function () {
        btn.classList.remove('btm-atc--added');
      }, 600);
    });

    // Error - clear adding state
    document.addEventListener('cart:error', function () {
      if (!activeBtn) return;
      activeBtn.classList.remove('btm-atc--adding', 'btm-atc--added');
      activeBtn = null;
    });
  }

  /* ─────────────────────────────────────────────
     9. Scroll Snap Setup
     Adds scroll-snap-align:start to the .shopify-section
     wrappers that contain a .btm-snap-section element.
     The <main> tag needs class="btm-scroll-snap" (see theme.liquid).
  ───────────────────────────────────────────── */
  function initScrollSnap() {
    var snapSections = document.querySelectorAll('.btm-snap-section');
    snapSections.forEach(function (inner) {
      // Promote snap-align to the Shopify section wrapper
      var wrapper = inner.closest('.shopify-section') || inner;
      wrapper.style.scrollSnapAlign = 'start';
    });
  }

  /* ─────────────────────────────────────────────
     Bootstrap - runs after DOM is ready
  ───────────────────────────────────────────── */
  function init() {
    initProgressBar();
    initScrollReveal();
    initStagger();
    initCountUp();
    initDrawOn();
    initParallax();
    initNavbarTheme();
    initScrollSnap();
    initATCAnimation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
